import { useEffect } from "react";
import { View, FlatList, Text, StyleSheet } from "react-native";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useDevice } from "../../../utils/hooks/useDevice";
import SvaraModal from "./SvaraModal";
import { BtnCloseModal } from "../../../assets";
import { rankModalStyles } from "./RankingComponents/styles";
import {
  jackpotHistoryRequest,
  JackpotHistoryItem,
} from "../../../store/jackpot/miniGamesRewardsActions";
import {
  selectJackpotHistory,
  selectJackpotHistoryLoading,
  selectJackpotHistoryError,
} from "../../../store/jackpot/miniGamesRewardsReducer";
import { FormatWithThousandSeparator } from "../../../utils/helpers/numberFormat";
import { formatDate } from "../../../utils/helpers/date";

interface IProps {
  onClose: () => void;
}

function formatTime(dateString: string): string {
  const d = new Date(dateString);
  return d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getGameNameTranslation(gameName: string, t: any): string {
  const gameNameLower = gameName.toLowerCase();
  if (gameNameLower.includes("black") || gameNameLower.includes("red")) {
    return t("T_SITE_MODAL_JACKPOT_GAME_BLACK_AND_RED");
  }
  if (gameNameLower.includes("slot")) {
    return t("T_SITE_MODAL_JACKPOT_GAME_SLOT");
  }
  if (gameNameLower.includes("scratch")) {
    return t("T_SITE_MODAL_JACKPOT_GAME_SCRATCH_CARD");
  }
  return gameName;
}

function JackpotHistoryRow({ item }: { item: JackpotHistoryItem }) {
  const { t } = useTranslation();
  const formattedAmount = FormatWithThousandSeparator(item.amount, {
    hideFractIfInteger: true,
  });
  const formattedDate = formatDate(item.createdAt);
  const formattedTime = formatTime(item.createdAt);
  const gameName = getGameNameTranslation(item.gameName, t);

  return (
    <View style={styles.row}>
      <Text style={[styles.cell, styles.nameCell]}>{item.playerId}</Text>
      <Text style={[styles.cell, styles.dateCell]}>{formattedDate}</Text>
      <Text style={[styles.cell, styles.timeCell]}>{formattedTime}</Text>
      <Text style={[styles.cell, styles.chipsCell]}>{formattedAmount}</Text>
      <Text style={[styles.cell, styles.gameCell]}>{gameName}</Text>
    </View>
  );
}

function JackpotHistoryModal({ onClose }: IProps) {
  const dispatch = useDispatch();
  const history = useSelector(selectJackpotHistory);
  const loading = useSelector(selectJackpotHistoryLoading);
  const error = useSelector(selectJackpotHistoryError);
  const { isMobile, height, isXs } = useDevice();
  const { t } = useTranslation();

  useEffect(() => {
    dispatch(jackpotHistoryRequest());
  }, [dispatch]);

  const listH = height * 0.75;

  return (
    <SvaraModal
      visible
      onClose={onClose}
      closeIcon={BtnCloseModal}
      closeButtonSize="big"
      closeButtonStyles={{
        width: 42,
        height: 42,
        ...(isXs && { transform: [{ translateX: 11 }, { translateY: -10 }] }),
      }}
    >
      {/* Title */}
      <Text style={styles.title}>{t("T_SITE_MODAL_JACKPOT_TITLE")}</Text>

      {/* Header row */}
      <View style={styles.headerRow}>
        <Text style={[styles.headerCell, styles.nameHeader]}>
          {t("T_SITE_MODAL_JACKPOT_TABLE_NAME")}
        </Text>
        <Text style={[styles.headerCell, styles.dateHeader]}>
          {t("T_SITE_MODAL_JACKPOT_TABLE_DATE")}
        </Text>
        <Text style={[styles.headerCell, styles.timeHeader]}>
          {t("T_SITE_MODAL_JACKPOT_TABLE_TIME")}
        </Text>
        <Text style={[styles.headerCell, styles.chipsHeader]}>
          {t("T_SITE_MODAL_JACKPOT_TABLE_CHIPS")}
        </Text>
        <Text style={[styles.headerCell, styles.gameHeader]}>
          {t("T_SITE_MODAL_JACKPOT_TABLE_GAME")}
        </Text>
      </View>

      {loading && (
        <View style={styles.centerContainer}>
          <Text style={styles.loadingText}>Loading...</Text>
        </View>
      )}

      {error && (
        <View style={styles.centerContainer}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}

      {!loading && !error && history && history.length === 0 && (
        <View style={styles.centerContainer}>
          <Text style={styles.emptyText}>No jackpot history found</Text>
        </View>
      )}

      {!loading && !error && history && history.length > 0 && (
        <FlatList
          style={{
            height: listH,
            ...rankModalStyles.flatList,
          }}
          data={history}
          showsVerticalScrollIndicator={false}
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => <JackpotHistoryRow item={item} />}
        />
      )}
    </SvaraModal>
  );
}

const styles = StyleSheet.create({
  title: {
    color: "white",
    fontSize: 22,
    fontWeight: "700",
    textAlign: "center",
    marginBottom: 16,
    textTransform: "uppercase",
  },
  headerRow: {
    flexDirection: "row",
    paddingHorizontal: 16,
    marginBottom: 8,
    alignItems: "center",
    width: "100%",
  },
  headerCell: {
    color: "#ffffffdc",
    fontSize: 14,
    fontWeight: "700",
  },
  nameHeader: {
    flex: 1.5,
  },
  dateHeader: {
    flex: 1,
  },
  timeHeader: {
    flex: 0.8,
  },
  chipsHeader: {
    flex: 1.2,
    textAlign: "right",
  },
  gameHeader: {
    flex: 1.5,
    textAlign: "left",
  },
  row: {
    flexDirection: "row",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#ffffff20",
    alignItems: "center",
  },
  cell: {
    color: "white",
    fontSize: 14,
  },
  nameCell: {
    flex: 1.5,
  },
  dateCell: {
    flex: 1,
  },
  timeCell: {
    flex: 1,
  },
  chipsCell: {
    flex: 1.2,
    textAlign: "right",
  },
  gameCell: {
    flex: 1.5,
    textAlign: "left",
  },
  centerContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 40,
  },
  loadingText: {
    color: "white",
    fontSize: 16,
  },
  errorText: {
    color: "#ff6b6b",
    fontSize: 16,
  },
  emptyText: {
    color: "#ffffff80",
    fontSize: 16,
  },
});

export default JackpotHistoryModal;
